import React from 'react';
import { Helmet } from 'react-helmet-async';

// CANONICAL URL CONFIGURATION
// Use the EXACT production domain - no trailing slash, no www, always https
const CANONICAL_DOMAIN = 'https://renaissancegardendubrovnik.com';

// Always return the canonical domain, never use window.location
const getBaseUrl = () => CANONICAL_DOMAIN;

const SEOSchema = ({ faqData, page = 'home' }) => {
  const baseUrl = getBaseUrl();
  
  // VacationRental Schema with Review data
  const vacationRentalSchema = {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "name": "Renaissance Garden Dubrovnik",
    "description": "Historic 16th-century waterfront villa with heated pool, 1000m² Renaissance garden and Adriatic sea views in Mokošica, Dubrovnik, Croatia. Accommodates up to 6 guests in 3 luxury bedrooms.",
    "url": baseUrl,
    "image": [
      `${baseUrl}/api/uploads/og-hero-image.webp`,
      "https://a0.muscache.com/im/pictures/caeec492-61c4-4449-a4d8-ff805faa06a2.jpg?im_w=1200",
      "https://a0.muscache.com/im/pictures/hosting/Hosting-36517649/original/3122c74f-c395-4948-a348-b16f97f68341.jpeg?im_w=1200"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mokošica",
      "addressLocality": "Dubrovnik",
      "addressRegion": "Dubrovnik-Neretva County",
      "postalCode": "20000",
      "addressCountry": "HR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 42.6507,
      "longitude": 18.0944
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Heated Swimming Pool", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Waterfront Location", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Renaissance Garden", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Air Conditioning", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Free WiFi", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Private Parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Sea View", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Historic Architecture", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Private Sea Access", "value": true }
    ],
    "numberOfBedrooms": 3,
    "occupancy": {
      "@type": "QuantitativeValue",
      "maxValue": 6
    },
    "floorSize": {
      "@type": "QuantitativeValue",
      "value": 1000,
      "unitCode": "MTK"
    },
    "petsAllowed": false,
    "tourBookingPage": `${baseUrl}/contact`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "57",
      "bestRating": "5"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Sarah M."
        },
        "datePublished": "2024-08-15",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "An absolute gem! The garden is breathtaking and the villa itself is like stepping back in time. Perfect for a romantic getaway."
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Michael R."
        },
        "datePublished": "2024-09-22",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Slavica and Marijo were wonderful hosts. The property exceeded our expectations. The pool area and gardens are magnificent!"
      }
    ]
  };

  // FAQ Schema REMOVED - now only in static index.html to avoid duplicate detection

  // Local Business Schema (removed empty telephone field)
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Renaissance Garden Dubrovnik",
    "description": "Historic 16th-century waterfront villa with heated pool and Renaissance garden in Mokošica, Dubrovnik, Croatia",
    "url": baseUrl,
    "email": "info@renaissancegardendubrovnik.com",
    "image": `${baseUrl}/api/uploads/og-hero-image.webp`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mokošica",
      "addressLocality": "Dubrovnik",
      "addressRegion": "Dubrovnik-Neretva County",
      "postalCode": "20000",
      "addressCountry": "Croatia"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 42.6507,
      "longitude": 18.0944
    },
    "sameAs": [
      "https://www.airbnb.com/rooms/36517649"
    ],
    "priceRange": "$$$"
  };

  // BreadcrumbList Schema for interior pages
  const getBreadcrumbSchema = () => {
    const breadcrumbs = {
      garden: [
        { name: "Home", url: baseUrl },
        { name: "The Garden", url: `${baseUrl}/garden` }
      ],
      gallery: [
        { name: "Home", url: baseUrl },
        { name: "Photo Gallery", url: `${baseUrl}/gallery` }
      ],
      contact: [
        { name: "Home", url: baseUrl },
        { name: "Contact Us", url: `${baseUrl}/contact` }
      ]
    };

    if (!breadcrumbs[page]) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs[page].map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };
  };

  // Page-specific meta data
  const pageMetaData = {
    home: {
      title: "Renaissance Garden Dubrovnik | Luxury Waterfront Villa & Heated Pool",
      description: "Historic 16th-century waterfront villa with heated pool, 1000m² Renaissance garden and Adriatic sea views in Mokošica, Dubrovnik, Croatia. Rated 5.0 stars by 57+ guests.",
      canonical: baseUrl
    },
    garden: {
      title: "The Renaissance Garden | Historic 16th Century Garden in Dubrovnik",
      description: "Explore our 1000m² Renaissance garden dating from the 16th century. One of the best-preserved historic gardens in the Dubrovnik-Neretva region with stone pathways and Mediterranean plants.",
      canonical: `${baseUrl}/garden`
    },
    gallery: {
      title: "Photo Gallery | Renaissance Garden Dubrovnik Villa",
      description: "Browse photos of our luxury waterfront villa in Dubrovnik. View the heated pool, Renaissance garden, bedrooms, and stunning Adriatic sea views.",
      canonical: `${baseUrl}/gallery`
    },
    contact: {
      title: "Contact & Booking | Renaissance Garden Dubrovnik",
      description: "Check availability and book your stay at Renaissance Garden Dubrovnik. Direct inquiries welcome for this luxury waterfront villa with heated pool.",
      canonical: `${baseUrl}/contact`
    }
  };

  const currentPageMeta = pageMetaData[page] || pageMetaData.home;
  const breadcrumbSchema = getBreadcrumbSchema();

  // Determine current page URL for hreflang self-reference
  const currentUrl = currentPageMeta.canonical;

  return (
    <Helmet>
      {/* Page-specific title and meta */}
      <title>{currentPageMeta.title}</title>
      <meta name="description" content={currentPageMeta.description} />
      
      {/* CANONICAL - Single source of truth, absolute URL, no trailing slash */}
      <link rel="canonical" href={currentPageMeta.canonical} />
      
      {/* HREFLANG - Self-referencing + x-default (required for canonical consistency) */}
      <link rel="alternate" hrefLang="en" href={currentPageMeta.canonical} />
      <link rel="alternate" hrefLang="x-default" href={currentPageMeta.canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={currentPageMeta.title} />
      <meta property="og:description" content={currentPageMeta.description} />
      <meta property="og:url" content={currentPageMeta.canonical} />
      <meta property="og:image" content={`${baseUrl}/api/uploads/og-hero-image.webp`} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Renaissance Garden Dubrovnik" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentPageMeta.title} />
      <meta name="twitter:description" content={currentPageMeta.description} />
      <meta name="twitter:image" content={`${baseUrl}/api/uploads/og-hero-image.webp`} />
      
      {/* VacationRental Schema */}
      <script type="application/ld+json">
        {JSON.stringify(vacationRentalSchema)}
      </script>
      
      {/* LodgingBusiness Schema */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {/* BreadcrumbList Schema for interior pages */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOSchema;
